import { useMemo } from "react";
import { useMapLayer } from "../hooks/useMapLayer";
import AreaObject, {
    AreaHighlightOverlay,
    AreaLabel,
    AreaSelectionOverlay,
} from "../objects/AreaObject";
import type {
    AreaHighlightStyle,
    AreaMapObject,
    MapLayerProps,
} from "../types";

interface AreasLayerProps extends MapLayerProps<AreaMapObject> {
    highlights?: ReadonlyMap<string, AreaHighlightStyle>;
}

const AreasLayer = ({
    id,
    items,
    selectedIds,
    highlights,
    onItemClick,
    onItemHover,
    onItemLeave,
}: AreasLayerProps) => {
    const {
        items: layerItems,
        itemsById,
        selectedItems,
    } = useMapLayer({ items, selectedIds });
    const highlightedItems = useMemo(
        () =>
            highlights
                ? Array.from(highlights, ([id, style]) => {
                      const item = itemsById.get(id);
                      return item ? { item, style } : undefined;
                  }).filter(
                      (
                          highlight,
                      ): highlight is {
                          item: AreaMapObject;
                          style: AreaHighlightStyle;
                      } => highlight !== undefined,
                  )
                : [],
        [highlights, itemsById],
    );
    const baseObjects = useMemo(
        () =>
            layerItems.map((item) => (
                <AreaObject
                    key={item.id}
                    item={item}
                    layerId={id}
                    onItemClick={onItemClick}
                    onItemHover={onItemHover}
                    onItemLeave={onItemLeave}
                />
            )),
        [id, layerItems, onItemClick, onItemHover, onItemLeave],
    );
    const labels = useMemo(
        () => layerItems.map((item) => <AreaLabel key={item.id} item={item} />),
        [layerItems],
    );

    return (
        <pixiContainer label="areas-layer">
            {baseObjects}
            {highlightedItems.map(({ item, style }) => (
                <AreaHighlightOverlay key={item.id} item={item} style={style} />
            ))}
            {selectedItems.map((item) => (
                <AreaSelectionOverlay key={item.id} item={item} />
            ))}
            {labels}
        </pixiContainer>
    );
};

export default AreasLayer;
